import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import {
  Layout, Error
} from 'components/organisms';
import {
  Title, HelperTitle, Text, Spacer, Button, Icon, LoadingSpinner
} from 'components/atoms';
import { toast } from 'react-toastify';
import { Tooltip, ModalConfirm } from 'components/molecules';
import { generateSubwebPath } from 'services/generators'
import { useMutation } from 'react-query'
import { useUserContext } from 'context/UserContext';
import { useToggle } from 'hooks';
import { Link, useParams, useHistory } from 'react-router-dom';
import { injectIntl } from 'react-intl'
import { intlType } from 'types'
import { useEditorContext, actions } from 'context/EditorContext';
import { deleteSubweb } from 'services/requests'
import {
  PagesUserList,
  SubwebsPagesTable,
  PageCreateModal,
  Authenticator,
  Toast
} from 'containers'
import { useSelector } from 'react-redux'

const Page = ({ intl }) => {
  const [{ user }] = useUserContext();
  const { subweb } = useParams()
  const [isOpen, toggle] = useToggle()
  const history = useHistory()
  const [, dispatch] = useEditorContext();

  const [isOpenConfirmation, toggle2] = useToggle(false)
  const { isLoading, isLoadingTable, currentPage } = useSelector(
    (state) => state.subweb
  )
  const [handleDeleteSubweb, { isLoading2 }] = useMutation(deleteSubweb, {
    onSuccess: () => {
      dispatch({ type: actions.PAGE_DELETE })
      toast.success(
        <Toast
          type="success"
          title="Subweb apagada."
        />
      );
      history.push('/')
    },
    onError: (err) => {
      toast.error(
        <Toast
          type="error"
          title="Erro a apagar a subweb."
          description={err.message}
        />
      );
    }
  })
  const subwebData = useMemo(() => {
    if (!user || !user.subwebs || !user.subwebs.length > 0) return null
    const data = user.subwebs.find(
      subwebData => subwebData.slug === subweb
        || subwebData.slug === decodeURIComponent(subweb)
    )
    return data
  }, [subweb, user])

  const subwebUrl = useMemo(
    () => generateSubwebPath({ subweb, withDomain: true, withRootHome: true }),
    [subweb])

  const error = useMemo(() => {
    if (!subweb) {
      return {
        message: 'Subweb não encontrada',
        code: 404
      }
    }
    return {
      message: `Sem permissões para editar a subweb: ${subweb}`,
      code: 401
    }
  }, [subweb])

  if (!subwebData) {
    return (
      <Layout modifiers={['center']}>
        <Error
          message={error.message}
          code={error.code}
          helper={(
            <Text>
              <Link to="/">Voltar à página inicial</Link>
            </Text>
          )}
        />
      </Layout>
    )
  }

  const handleConfirm = (hasConfirmed) => {
    if (hasConfirmed) {
      handleDeleteSubweb({
        subweb
      })
    }
    toggle2()
  }

  return (
    <Layout>
      <ModalConfirm
        isOpen={isOpenConfirmation}
        toggle={toggle2}
        onChange={handleConfirm}
        title="Apagar subweb"
        message="Depois de apagada, a página deixará de estar disponível em todos os idiomas e não será possível recuperar a informação. Tem a certeza que pretende continuar?"
        textConfirm="Sim"
        textDiscard="Cancelar"
      />
      <Layout.Header>
        <Layout.Title>
          <Title>{subwebData.description}</Title>
          <InlineAnchor href={subwebUrl} target="_blank" rel="noopener noreferrer">
            <Text modifiers={['light']}>
              {subwebData.slug}
            </Text>
            <Spacer left="xs3">
              <Icon icon="fal fa-external-link" height={12} />
            </Spacer>
          </InlineAnchor>
        </Layout.Title>
        <Layout.Actions>
          <PageCreateModal isOpen={isOpen} toggle={toggle} />
          <Button modifiers={['dark', 'stretchY']} onClick={toggle}>
            {intl.formatMessage({ id: 'page.new' })}
          </Button>
          <Spacer right="xs2" />
          <Link to={`/${encodeURIComponent(subweb)}/editor/pt/nav`}>
            <Button modifiers={['dark', 'stretchY']}>
              {intl.formatMessage({ id: 'subweb.edit.menu' })}
            </Button>
          </Link>
          <Spacer right="xs2" />

          {/* Admin only */}
          <Authenticator requires="admin" subweb={subweb}>
            <Link to={`/${encodeURIComponent(subweb)}/people`}>
              <Button modifiers={['dark', 'stretchY']}>
                {intl.formatMessage({ id: 'subweb.people' })}
              </Button>
            </Link>
            <Spacer right="xs2" />
            <Link to={`/${encodeURIComponent(subweb)}/permissions`}>
              <Button modifiers={['dark', 'stretchY']}>
                {intl.formatMessage({ id: 'subweb.permissions' })}
              </Button>
            </Link>
            <Spacer right="xs2" />
            <Link to={`/${encodeURIComponent(subweb)}/settings`}>
              <Button modifiers={['dark', 'stretchY']}>
                {intl.formatMessage({ id: 'subweb.settings' })}
              </Button>
            </Link>
            <Spacer right="xs2" />
            <Button modifiers={['danger', 'stretchY']} onClick={toggle2} loading={isLoading2}>
              {intl.formatMessage({ id: 'subwebs.delete' })}
            </Button>
            <Spacer right="xs2" />
          </Authenticator>

          <Tooltip tooltip="visitar subweb no portal" placement="bottom">
            <a href={subwebUrl} target="_blank" rel="noopener noreferrer">
              <Button modifier={['stretchY']}>
                {intl.formatMessage({ id: 'subwebs.visit' })}
              </Button>
            </a>
          </Tooltip>
        </Layout.Actions>
      </Layout.Header>
      <Spacer bottom="xl2">
        <SectionTitle>
          <HelperTitle modifiers={['bottomMargin']}>
            {intl.formatMessage({ id: 'section.quick.page' })}
          </HelperTitle>
          {(isLoading || (isLoadingTable && currentPage === 1)) && (
            <Spacer left="xs2" bottom="l">
              <LoadingSpinner />
            </Spacer>
          )}
        </SectionTitle>
        <PagesUserList subweb={subweb} />
      </Spacer>
      <HelperTitle modifiers={['bottomMargin']}>
        {intl.formatMessage({ id: 'section.table.page' })}
      </HelperTitle>
      <SubwebsPagesTable subweb={subweb} />
    </Layout>
  );
}

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
`

const InlineAnchor = styled.a`
  display: flex;
  align-items: center;
`

Page.propTypes = {
  intl: intlType.isRequired,
}

export default injectIntl(Page);
